import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type PrayerSectionItem = {
  id: number;
  hebrew: string;
  translation: string;
  /** "instruction" = small font (who says what); omit or "reading" = main text */
  type?: 'instruction' | 'reading';
};

export type PrayerTextData = {
  titleHe: string;
  titleEn: string;
  sections: PrayerSectionItem[];
};

type PrayerTextSectionProps = {
  data: PrayerTextData;
  titleTKey: string;
  rabbiAudioUrl: string | null;
  direction: 'rtl' | 'ltr';
  isHebrew: boolean;
};

const BLUE_ACCENT = '#3B7BF4';
const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const BORDER_LIGHT = '#D1D5DB';

const PrayerTextSection: React.FC<PrayerTextSectionProps> = ({
  data,
  titleTKey,
  rabbiAudioUrl,
  direction,
  isHebrew,
}) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);

  const title = t(titleTKey, isHebrew ? data.titleHe : data.titleEn);

  const handlePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      if (audio.ended || audio.currentTime >= audio.duration) {
        audio.currentTime = 0;
      }
      try {
        await audio.play();
      } catch (e) {
        console.error('PrayerTextSection: audio play failed, url:', rabbiAudioUrl, e);
        setPlaying(false);
      }
    }
  };

  return (
    <section
      style={{
        background: '#ffffff',
        borderRadius: 20,
        padding: '18px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
        border: `1px solid ${BORDER_LIGHT}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        direction,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <h3
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: TEXT_DARK,
            margin: 0,
          }}
        >
          {title}
        </h3>
        {rabbiAudioUrl && (
          <button
            type="button"
            onClick={handlePlayPause}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 16px',
              borderRadius: 14,
              background: BLUE_ACCENT,
              color: 'white',
              border: 'none',
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {playing ? (
              <>
                <span>⏸</span>
                {t('pause', 'Pause')}
              </>
            ) : (
              <>
                <span>▶</span>
                {t('play', 'Play')} {t('rabbi_recording', "Rabbi's recording")}
              </>
            )}
          </button>
        )}
      </div>
      {rabbiAudioUrl && (
        <audio
          ref={audioRef}
          src={rabbiAudioUrl}
          preload="auto"
          onEnded={() => setPlaying(false)}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          onError={(e) => {
            console.error(
              'PrayerTextSection: audio error, url:',
              rabbiAudioUrl,
              (e.target as HTMLAudioElement).error,
            );
            setPlaying(false);
          }}
          style={{ display: 'none' }}
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {data.sections.map((section) => {
          const isInstruction = section.type === 'instruction';
          return (
            <div
              key={section.id}
              style={{
                background: isInstruction ? 'transparent' : '#f9fafb',
                borderRadius: 12,
                padding: isInstruction ? '4px 0' : '14px 16px',
                border: isInstruction ? 'none' : `1px solid ${BORDER_LIGHT}`,
              }}
            >
              <div
                dir="rtl"
                style={{
                  fontFamily: isInstruction
                    ? "var(--kol-font-sans, 'Inter', system-ui, sans-serif)"
                    : '"Noto Serif Hebrew", "David Libre", serif',
                  fontSize: isInstruction ? 13 : 18,
                  lineHeight: 1.6,
                  color: isInstruction ? TEXT_SECONDARY : TEXT_DARK,
                  marginBottom: isHebrew ? 0 : 8,
                  fontWeight: isInstruction ? 500 : 400,
                }}
              >
                {section.hebrew}
              </div>
              {!isHebrew && section.translation && (
                <div
                  style={{
                    fontSize: isInstruction ? 12 : 14,
                    color: TEXT_SECONDARY,
                    lineHeight: 1.4,
                  }}
                >
                  {section.translation}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PrayerTextSection;
